import type { IReactionGroup, IReactionSubject } from "types/graphqlTypes";
import { IReactionContent } from "types/graphqlTypes";
import { PopoverContent, PopoverTrigger, Popover } from "components/ui/Popover";
import { FaceSmileIcon } from "@heroicons/react/24/outline";
import classNames from "components/ui/classNames";
import type { InternalRefetchQueriesInclude } from "@apollo/client";
import { useMutation } from "@apollo/client";
import type {
  IAddReactionMutation,
  IAddReactionMutationVariables,
} from "components/reactions/mutations/__generated__/AddReaction.generated";
import AddReaction from "components/reactions/mutations/AddReaction";
import type {
  IRemoveReactionMutation,
  IRemoveReactionMutationVariables,
} from "components/reactions/mutations/__generated__/RemoveReaction.generated";
import RemoveReaction from "components/reactions/mutations/RemoveReaction";
import type { IReactableFieldsFragment } from "lib/graphql/fragments/__generated__/ReactableFields.generated";

interface IPostReactions {
  subjectId: string;
  subjectType: IReactionSubject;
  reactable: IReactableFieldsFragment;
  refetchQueries: InternalRefetchQueriesInclude;
}

export default function PostReactions({ subjectId, subjectType, reactable, refetchQueries }: IPostReactions) {
  const { reactionGroups, viewerCanReact } = reactable;

  const [addReaction, { loading: isAddingReaction }] = useMutation<IAddReactionMutation, IAddReactionMutationVariables>(
    AddReaction,
    {
      refetchQueries,
    },
  );

  const [removeReaction, { loading: isRemovingReaction }] = useMutation<
    IRemoveReactionMutation,
    IRemoveReactionMutationVariables
  >(RemoveReaction, {
    refetchQueries,
  });

  const isLoading = isAddingReaction || isRemovingReaction;

  function handleReactionClick(content: IReactionContent, reaction?: IReactionGroup) {
    if (reaction && reaction.viewerHasReacted) {
      removeReaction({
        variables: {
          input: {
            content,
            subjectId,
            subjectType,
          },
        },
      });
    } else {
      addReaction({
        variables: {
          input: {
            content,
            subjectId,
            subjectType,
          },
        },
      });
    }
  }

  return (
    <div className="flex flex-wrap gap-x-1.5 gap-y-1.5">
      {EMOJIS.sort((a, b) => (a.key < b.key ? 1 : -1)).map((emoji) => {
        const reaction = reactionGroups.find((node) => node.content === emoji.key);
        if (!reaction) {
          return null;
        }

        return (
          <button
            key={emoji.key}
            type="button"
            disabled={isLoading}
            className={classNames("flex items-center space-x-2 rounded-full border border-neutral-200 py-1 pl-1 pr-2", {
              "border-blue-300 bg-blue-50": reaction.viewerHasReacted,
            })}
            onClick={() => handleReactionClick(emoji.key, reaction)}
          >
            {emoji.renderIcon()}
            <span className="font-aeonik text-sm font-medium text-blue-600">{reaction.totalReactions}</span>
          </button>
        );
      })}

      {viewerCanReact && (
        <Popover>
          <PopoverTrigger
            className="rounded-full border border-neutral-200 p-1 hover:border-blue-300 hover:bg-blue-50"
            disabled={isLoading}
          >
            <FaceSmileIcon className="h-5 w-5 stroke-gray-500 hover:stroke-blue-500" />
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="flex space-x-0.5 p-0.5">
            {EMOJIS.sort((a, b) => (a.key < b.key ? 1 : -1)).map((emoji) => {
              const reaction = reactionGroups.find((node) => node.content === emoji.key);

              return (
                <button
                  key={emoji.key}
                  type="button"
                  disabled={isLoading}
                  className={classNames("rounded-lg p-2 hover:bg-blue-50", {
                    "bg-blue-50": reaction && reaction.viewerHasReacted,
                  })}
                  onClick={() => handleReactionClick(emoji.key, reaction)}
                >
                  {emoji.renderIcon()}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export const EMOJIS = [
  {
    key: IReactionContent.ThumbsUp,
    renderIcon: () => (
      <span role="img" aria-label="thumbs up">
        👍
      </span>
    ),
  },
  {
    key: IReactionContent.Laugh,
    renderIcon: () => (
      <span role="img" aria-label="grinning face with smiling eyes">
        😄
      </span>
    ),
  },
  {
    key: IReactionContent.Hooray,
    renderIcon: () => (
      <span role="img" aria-label="party popper">
        🎉
      </span>
    ),
  },
  {
    key: IReactionContent.Confused,
    renderIcon: () => (
      <span role="img" aria-label="confused face">
        😕
      </span>
    ),
  },
  {
    key: IReactionContent.Heart,
    renderIcon: () => (
      <span role="img" aria-label="red heart">
        ❤️️
      </span>
    ),
  },

  {
    key: IReactionContent.Rocket,
    renderIcon: () => (
      <span role="img" aria-label="rocket">
        🚀
      </span>
    ),
  },
  {
    key: IReactionContent.Eyes,
    renderIcon: () => (
      <span role="img" aria-label="eyes">
        👀
      </span>
    ),
  },
];
